import { useState, useEffect, useCallback } from 'react';
import { 
  isStandalone, 
  isPWAMode, 
  isBrowserMode, 
  getDisplayMode,
  isPWAInstallSupported,
  isIOS, 
  isAndroid, 
  isMobileDevice,
  isIncognito
} from '../utils/deviceDetect';

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null;
  }
}

export function useSimplePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSDevice] = useState(isIOS);
  const [isAndroidDevice] = useState(isAndroid);
  const [isMobile] = useState(isMobileDevice);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [displayMode, setDisplayMode] = useState<string>('browser');
  const [installSupported, setInstallSupported] = useState(false);
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);

  // Check if the app is already installed and update status
  useEffect(() => {
    const checkInstallationStatus = async () => {
      const installed = isStandalone();
      const currentDisplayMode = getDisplayMode();
      const supported = isPWAInstallSupported();
      const incognito = await isIncognito();
      
      setIsInstalled(installed);
      setDisplayMode(currentDisplayMode);
      setInstallSupported(supported && !incognito);
      setIsIncognitoMode(incognito);
      
      // Only show banner if not installed, not dismissed, and install is supported
      if (!installed && !installDismissed && supported && !incognito) {
        setShowInstallBanner(true);
      }
    };

    checkInstallationStatus();
  }, [installDismissed]);

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(promptEvent);
      setCanInstall(true);
      (window as any).deferredPrompt = promptEvent;
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setCanInstall(false);
      setShowInstallBanner(false);
      setInstallPrompt(null);
      (window as any).deferredPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Simple install handler
  const handleInstallClick = useCallback(async (): Promise<boolean> => {
    console.log('Install click triggered', { 
      isIOSDevice, 
      installPrompt: !!installPrompt, 
      canInstall,
      deferredPrompt: !!(window as any).deferredPrompt,
      isInstalled
    });

    // Check if already installed
    if (isInstalled || isPWAMode()) {
      console.log('App is already installed');
      return true;
    }

    // For iOS Safari, show instructions instead of trying to trigger prompt
    if (isIOSDevice) {
      console.log('iOS device detected, showing instructions');
      return false; // This will trigger showing instructions
    }

    // Try to use the stored prompt
    let promptToUse = installPrompt || (window as any).deferredPrompt;
    
    if (promptToUse) {
      try {
        console.log('Showing native install prompt');
        await promptToUse.prompt();
        const { outcome } = await promptToUse.userChoice;
        
        console.log('Install prompt outcome:', outcome);
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setIsInstalled(true);
          setCanInstall(false);
          setInstallPrompt(null);
          setShowInstallBanner(false);
          // Clear the global prompt
          (window as any).deferredPrompt = null;
          return true;
        } else {
          console.log('User dismissed the install prompt');
          return false;
        }
      } catch (error) {
        console.error('Error showing install prompt:', error);
        return false;
      }
    }

    // If no native prompt available, show instructions
    console.log('No native install prompt available, showing instructions');
    return false;
  }, [installPrompt, isIOSDevice, canInstall, isInstalled]);

  // Dismiss install banner
  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    setInstallDismissed(true);
    // Store dismissal in localStorage
    localStorage.setItem('pwa-install-dismissed', 'true');
  }, []);

  // Check if banner was previously dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      setInstallDismissed(true);
      setShowInstallBanner(false);
    }
  }, []);

  return {
    // Installation state
    isInstalled,
    canInstall,
    showInstallBanner,
    installSupported,
    
    // Device detection
    isIOSDevice,
    isAndroidDevice,
    isMobile,
    isIncognito: isIncognitoMode,
    
    // Display mode
    displayMode,
    
    // Actions
    handleInstallClick,
    dismissInstallBanner,
    
    // Legacy compatibility
    installPrompt,
    setInstallPrompt,
    setCanInstall,
    setIsInstalled,
    setShowInstallBanner
  };
}
